/**
 * The shell's BASE-VERSION memory (spec-artifact-locking §8): "Paimti
 * darbui" remembers which version the user started editing, and the later
 * „Grąžinti" declares it as `metadata.baseVersionNumber` — so the server
 * can refuse an upload that would silently overwrite a colleague's
 * check-in. The user never sees a version number; this module is why.
 *
 * WHY IT MUST SURVIVE A RELOAD: taking a file and returning it are minutes
 * or hours apart, with SketchUp in between — the tab is closed, refreshed
 * or moved to another window. In-memory state would forget the base in
 * exactly the case the guard exists for, so the store is localStorage.
 *
 * KEYED PER SIGNED-IN USER: two employees share a workstation profile far
 * more often than they share an account, and inheriting a colleague's base
 * would declare an edit the user never made. One namespaced key per user,
 * dropped wholesale when the user is unknown.
 *
 * Pure + DOM-free (structural `KeyValueStorage`, no `window`), so it
 * compiles under both tsconfigs and is unit-tested with a fake storage in
 * test/ui/base-memory.test.ts. EVERY storage call is guarded: Safari
 * private mode throws on setItem, quota can be exhausted, and a corrupted
 * value must never break the browse screen — a failed store degrades to a
 * process-lifetime Map, which still covers the take→return round trip
 * inside one tab.
 */

/** The `localStorage` slice this module touches (structural — the browser
 * shell passes the real one, tests pass a fake, a host without storage
 * passes null). */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Namespace prefix — one key per signed-in user. */
export const BASE_MEMORY_KEY_PREFIX = "modulina.taken-base.";

export function baseMemoryKey(userKey: string): string {
  return `${BASE_MEMORY_KEY_PREFIX}${userKey.trim().toLowerCase()}`;
}

type BaseMap = Record<string, number>;

/**
 * Remembers {artifactId → the version number taken for work} for ONE user.
 * A missing storage or a missing user identity is not an error — the
 * memory simply lives in the instance (documented degradation above).
 */
export class TakenBaseMemory {
  private readonly key: string | null;
  private readonly storage: KeyValueStorage | null;
  /** In-memory mirror AND the fallback store when persistence is
   * unavailable — always authoritative for this instance's reads, so a
   * store that silently fails still answers correctly within the tab. */
  private readonly fallback: BaseMap = {};

  constructor(storage: KeyValueStorage | null, userKey: string | null) {
    const trimmed = userKey?.trim();
    this.key = trimmed ? baseMemoryKey(trimmed) : null;
    this.storage = this.key ? storage : null;
  }

  /** Record the version this artifact was taken for work at. Non-positive
   * or non-integer numbers are NOT stored: the domain schema requires a
   * positive integer base, so remembering a 0 would only produce a
   * `validation_failed` at return time (an artifact with no versions has
   * no base — see baseVersionToDeclare). */
  remember(artifactId: string, versionNumber: number): void {
    if (!Number.isInteger(versionNumber) || versionNumber <= 0) return;
    const map = this.read();
    map[artifactId] = versionNumber;
    this.write(map);
  }

  /** The remembered base, or null when this artifact was never taken (a
   * plain upload — the silent auto-lock path supplies its own base). */
  baseFor(artifactId: string): number | null {
    const value = this.read()[artifactId];
    return typeof value === "number" ? value : null;
  }

  /** Forget one artifact — after a successful return (the check-in
   * consumed the base) or an abandon (no upload will follow). Leaving it
   * would declare a stale base on the user's NEXT plain upload. */
  forget(artifactId: string): void {
    const map = this.read();
    if (!(artifactId in map)) return;
    delete map[artifactId];
    this.write(map);
  }

  private read(): BaseMap {
    if (!this.storage || !this.key) return { ...this.fallback };
    let raw: string | null;
    try {
      raw = this.storage.getItem(this.key);
    } catch {
      return { ...this.fallback };
    }
    if (!raw) return { ...this.fallback };
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Corrupted/foreign value: ignore it rather than crash the screen.
      // The next write overwrites it wholesale.
      return { ...this.fallback };
    }
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return { ...this.fallback };
    }
    const out: BaseMap = { ...this.fallback };
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === "number" && Number.isInteger(value) && value > 0) out[id] = value;
    }
    return out;
  }

  private write(map: BaseMap): void {
    for (const id of Object.keys(this.fallback)) delete this.fallback[id];
    Object.assign(this.fallback, map);
    if (!this.storage || !this.key) return;
    try {
      this.storage.setItem(this.key, JSON.stringify(map));
    } catch {
      // Private mode / quota: the in-memory fallback above already holds
      // the value, so the take→return round trip still works in this tab.
    }
  }
}

/** The browser's real store, when the host has one (a CEF dialog or a
 * server-side render may not). Never throws — a host that forbids storage
 * access answers null and the memory degrades to process lifetime. */
export function browserStorage(scope: {
  localStorage?: KeyValueStorage | null;
}): KeyValueStorage | null {
  try {
    return scope.localStorage ?? null;
  } catch {
    return null;
  }
}
